
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { Button } from "@/components/ui/button";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { translations, defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";
import { formatTextToHtml } from "@/utils/formatText";
import { DOCUMENT_VERSIONS, ACTIVE_CHANGELOG } from "@/lib/constants/documentVersions";
import { useChangelogVisibility } from "@/hooks/useChangelogVisibility";
import SubprocessorList from "@/components/legal/SubprocessorList";


const DISMISS_LABEL: Record<SupportedLanguage, string> = {
  en: 'Got it',
  de: 'Verstanden',
  fr: 'Compris',
  es: 'Entendido',
};

const CHANGELOG_COPY: Record<string, { heading: string; body: string }> = {
  en: {
    heading: "What changed on 15 August 2026",
    body: "PaperVerse Writer is now covered by these policies. New recipients disclosed: Google Cloud Run (EU/EEA) for LaTeX compilation and OpenAlex (USA) for literature search — only topical search terms leave the EU, never manuscript content or personal data. AI processing in the Writer is based on performance of the contract (Art. 6(1)(b) GDPR); your content is not used to train AI models. Statutory references were updated from TMG to DDG.",
  },
  de: {
    heading: "Was sich am 15. August 2026 geändert hat",
    body: "PaperVerse Writer ist nun von dieser Erklärung erfasst. Neue Empfänger offengelegt: Google Cloud Run (EU/EWR) für die LaTeX-Kompilierung und OpenAlex (USA) für die Literatursuche — die EU verlassen ausschließlich thematische Suchbegriffe, niemals Manuskriptinhalte oder personenbezogene Daten. Die KI-Verarbeitung im Writer erfolgt zur Vertragserfüllung (Art. 6 Abs. 1 S. 1 lit. b) DSGVO); Ihre Inhalte werden nicht zum Training von KI-Modellen verwendet. Gesetzesverweise wurden von TMG auf DDG aktualisiert.",
  },
  fr: {
    heading: "Ce qui a changé le 15 août 2026",
    body: "PaperVerse Writer est désormais couvert par la présente politique. Nouveaux destinataires divulgués : Google Cloud Run (UE/EEE) pour la compilation LaTeX et OpenAlex (États-Unis) pour la recherche bibliographique — seuls des termes de recherche thématiques quittent l'UE, jamais le contenu des manuscrits ni des données personnelles. Le traitement par IA dans le Writer repose sur l'exécution du contrat (art. 6, § 1, b) RGPD) ; vos contenus ne servent pas à l'entraînement de modèles d'IA. Les références légales ont été actualisées de la TMG vers la DDG.",
  },
  es: {
    heading: "Qué cambió el 15 de agosto de 2026",
    body: "PaperVerse Writer ya está cubierto por esta política. Nuevos destinatarios divulgados: Google Cloud Run (UE/EEE) para la compilación LaTeX y OpenAlex (EE. UU.) para la búsqueda bibliográfica: solo salen de la UE términos de búsqueda temáticos, nunca contenidos del manuscrito ni datos personales. El tratamiento por IA en el Writer se basa en la ejecución del contrato (art. 6, ap. 1, letra b) RGPD); sus contenidos no se utilizan para entrenar modelos de IA. Las referencias legales se actualizaron de TMG a DDG.",
  },
};

const formatVersionDate = (version: string, language: string): string => {
  const locale = language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'es' ? 'es-ES' : 'en-US';
  try {
    return new Date(version).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return version;
  }
};

// Helper to generate anchor ID from section title
const generateAnchorId = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/[äÄ]/g, 'ae')
    .replace(/[öÖ]/g, 'oe')
    .replace(/[üÜ]/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
};

// Stable, language-neutral anchors for sections referenced from external
// channels (e.g. invitation emails) where the recipient's UI language
// is unknown at link-construction time. Maps section titles (all locales)
// to a fixed slug. Edit only when the corresponding link target changes.
const STABLE_ANCHORS: Record<string, string> = {
  // Privacy Policy — invitations to third parties (Art. 14 disclosure).
  // Both the legacy review-only titles and the broadened share+review titles
  // resolve to the same slug so existing email links keep working.
  'Review-Einladungen an Dritte': 'review-invitations',
  'Review Invitations to Third Parties': 'review-invitations',
  'Invitations à Reviewer adressées à des Tiers': 'review-invitations',
  'Invitaciones de Revisión a Terceros': 'review-invitations',
  'Einladungen an Dritte (Review- und Freigabe-Einladungen)': 'review-invitations',
  'Invitations to Third Parties (Review and Share Invitations)': 'review-invitations',
  'Invitations adressées à des Tiers (Invitations à Reviewer et de Partage)': 'review-invitations',
  'Invitaciones a Terceros (Invitaciones de Revisión y de Compartición)': 'review-invitations',
};

// Render table of contents with links
const renderTableOfContents = (text: string, allSections: Array<{ title: string; text: string }>) => {
  const items = text.split('\n').filter(item => item.trim());
  
  return (
    <ul className="list-none space-y-3 pl-0">
      {items.map((item, idx) => {
        const trimmedItem = item.trim();
        // Find the matching section to get its anchor
        const matchingSection = allSections.find(s => s.title === trimmedItem);
        const anchorId = matchingSection ? generateAnchorId(matchingSection.title) : generateAnchorId(trimmedItem);
        
        return (
          <li key={idx} className="flex items-center gap-2">
            <ChevronRight className="h-4 w-4 text-primary flex-shrink-0" />
            <a 
              href={`#${anchorId}`}
              className="text-primary hover:underline"
            >
              {trimmedItem}
            </a>
          </li>
        );
      })}
    </ul>
  );
};

const PrivacyPolicy = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);
  const changelog = useChangelogVisibility({
    documentType: 'privacyPolicy',
    versionAnchor: ACTIVE_CHANGELOG.versionAnchor,
    expiresAt: ACTIVE_CHANGELOG.expiresAt,
  });

  useEffect(() => {
    // Check saved language preference
    const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && ['en', 'de', 'fr', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage as SupportedLanguage);
    } else {
      // If no saved language, detect browser language
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);
  
  const t = translations.legalPages[language]?.privacyPolicy;
  const allSections = t?.content || [];
  
  // Check if a section is the table of contents
  const isTableOfContents = (title: string): boolean => {
    const tocTitles = [
      'Inhaltsübersicht',           // German
      'Table of Contents',           // English
      'Índice de Contenidos',        // Spanish (actual title in es.ts)
      'Table des Matières'           // French (actual title in fr.ts)
    ];
    return tocTitles.includes(title);
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-grow pt-24">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-16">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t?.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold mb-6">{t?.title}</h1>

          {/* Changelog notice for the EU migration */}
          {changelog.visible && (
            <div
              id={`changelog-${ACTIVE_CHANGELOG.versionAnchor}`}
              className="mb-8 p-4 rounded-lg border border-primary/30 bg-primary/5"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-lg font-semibold m-0 text-foreground">
                  {CHANGELOG_COPY[language]?.heading ?? CHANGELOG_COPY.en.heading}
                </h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={changelog.dismiss}
                  className="shrink-0 -mt-1 -mr-2 h-8"
                >
                  <X className="h-4 w-4 mr-1" />
                  {DISMISS_LABEL[language]}
                </Button>
              </div>
              <p className="text-sm m-0 text-muted-foreground">
                {CHANGELOG_COPY[language]?.body ?? CHANGELOG_COPY.en.body}
              </p>
            </div>
          )}

          <div className="mb-8 p-4 rounded-lg border bg-muted/40">
            <p className="text-sm m-0 text-foreground/80">
              {language === 'de'
                ? 'Eine vollständige, laufend aktualisierte Übersicht aller Unterauftragsverarbeiter und Empfänger — mit Verarbeitungsort und Rechtsgrundlage — finden Sie im Abschnitt '
                : language === 'fr'
                ? 'Une liste complète et continuellement mise à jour de tous les sous-traitants et destinataires — avec le lieu de traitement et la base légale — figure dans la section '
                : language === 'es'
                ? 'Encontrará una lista completa y actualizada de todos los encargados y destinatarios —con lugar de tratamiento y base jurídica— en la sección '
                : 'A complete, continuously updated overview of all sub-processors and recipients — with processing location and legal basis — is available in the '}
              <a href="#sub-processors" className="text-primary hover:underline">
                {language === 'de'
                  ? 'Unterauftragsverarbeiter'
                  : language === 'fr'
                  ? 'sous-traitants'
                  : language === 'es'
                  ? 'encargados del tratamiento'
                  : 'sub-processor list'}
              </a>
              {language === 'de' || language === 'fr' || language === 'es' ? '' : ' section'}.
            </p>
          </div>


          <div className="prose prose-slate max-w-none">
            {allSections.map((section, index) => (
              <div key={index} id={generateAnchorId(section.title)}>
                {STABLE_ANCHORS[section.title] && (
                  <span id={STABLE_ANCHORS[section.title]} aria-hidden="true" />
                )}
                <h2>{section.title}</h2>
                {isTableOfContents(section.title) ? (
                  renderTableOfContents(section.text, allSections)
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: formatTextToHtml(section.text) }} />
                )}
              </div>
            ))}

            <SubprocessorList language={language} />


            <p className="text-sm text-muted-foreground mt-8">
              {language === 'de' ? 'Zuletzt aktualisiert' :
               language === 'fr' ? 'Dernière mise à jour' :
               language === 'es' ? 'Última actualización' :
               'Last updated'}: {formatVersionDate(DOCUMENT_VERSIONS.privacyPolicy, language)}
            </p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
